import { Link } from "react-router-dom";
import { FiAlertTriangle, FiChevronRight, FiClock, FiDollarSign, FiLock, FiPieChart, FiPlus } from "react-icons/fi";
import { formatNaira } from "../../../utils/dashboardFormat";
import { compactNaira } from "../../../components/MemberProfile/profileModel";
import { useGetMemberCategoriesSummaryQuery } from "../../../store/member/member-contribution";
import { KIND_META, normalizeMemberCategories, whenLabel } from "../../../components/Contributions/contribModel";
import { KindChip, Notice, PageHeader, Pill, Progress, Skeleton, StatusPill, Tile } from "../../../components/Contributions/parts";

const PAY = "/member/contribution/record";

/**
 * The top of "My contributions": what I have, what I can do, and where my money went category by category.
 * Choosing a category filters the history below it.
 */
export default function MemberContributionSummary({ totals, loading, onWithdraw, activeCategory, onPickCategory }) {
  const summary = useGetMemberCategoriesSummaryQuery();
  const missing = summary.isError && [404, 405].includes(summary.error?.status);
  const data = summary.data ? normalizeMemberCategories(summary.data) : null;
  const rows = data ? data.categories.filter((c) => c.paid > 0 || c.pending > 0 || c.perMember) : [];

  return (
    <div className="cb" style={{ paddingBottom: 8 }}>
      <PageHeader
        title="My contributions"
        sub="What you have put in, and where it went"
        actions={
          <span data-tour="member-contrib-record-cta">
            <Link to={PAY} className="cb-btn primary">
              <FiPlus size={16} aria-hidden="true" /> Pay a contribution
            </Link>
          </span>
        }
      />

      <div className="cb-tiles" role="group" aria-label="My totals">
        <Tile icon={FiDollarSign} tone="primary" label="Total contributed" value={loading ? "…" : compactNaira(totals.total)} quiet={!loading && !totals.total} />
        <div className="cb-tile tone-green" data-tour="member-contrib-savings">
          <span className="ico"><FiDollarSign size={16} aria-hidden="true" /></span>
          <div className="v">{loading ? "…" : compactNaira(totals.savings)}</div>
          <div className="l">Savings you can withdraw</div>
          {totals.savings > 0 ? (
            <button type="button" className="cb-btn sm" style={{ marginTop: 8 }} onClick={onWithdraw}>
              Withdraw savings
            </button>
          ) : null}
        </div>
        <div className="cb-tile tone-purple">
          <span className="ico"><FiPieChart size={16} aria-hidden="true" /></span>
          <div className="v">{loading ? "…" : compactNaira(totals.shares)}</div>
          <div className="l">Shares</div>
          <div className="cb-faint" style={{ marginTop: 4, display: "flex", gap: 4, alignItems: "center" }}>
            <FiLock size={12} aria-hidden="true" /> Locked until you exit
          </div>
        </div>
      </div>

      {data && data.pendingCount > 0 ? (
        <div className="cb-banner tone-amber" role="status">
          <FiClock size={16} aria-hidden="true" />
          <span>
            {data.pendingCount} {data.pendingCount === 1 ? "payment is" : "payments are"} waiting for confirmation ({formatNaira(data.pendingTotal)}). It will count once it's confirmed.
          </span>
        </div>
      ) : null}

      {missing ? null : (
        <div className="cb-card">
          <h2 style={{ fontSize: 16, marginBottom: 4 }}>Where my money went</h2>
          {activeCategory && data ? (
            <p className="cb-mute" style={{ marginBottom: 8 }} aria-live="polite">
              Showing payments for <strong style={{ color: "var(--cb-ink)" }}>{data.categories.find((c) => String(c.id) === String(activeCategory))?.name || "this category"}</strong>.{" "}
              <button type="button" className="cb-chip" onClick={() => onPickCategory("")}>Show all payments</button>
            </p>
          ) : (
            <p className="cb-mute" style={{ marginBottom: 8 }}>Pick a category to see its payments below.</p>
          )}

          {summary.isLoading ? <Skeleton rows={3} /> : null}
          {summary.isError && !missing ? (
            <Notice tone="amber" icon={FiAlertTriangle} title="Couldn't load your categories" actionLabel="Try again" onAction={() => summary.refetch()}>
              Your history below still works.
            </Notice>
          ) : null}

          {data && rows.length === 0 ? (
            <Notice title="Nothing here yet" actionLabel="Pay a contribution" actionTo={PAY}>
              Once you pay into a category, you'll see how much you've put in and what's still due.
            </Notice>
          ) : null}

          {data && rows.length > 0 ? (
            <div className="cb-cats" aria-label="My categories">
              {rows.map((c) => {
                const tone = KIND_META[c.kind].tone;
                const on = String(activeCategory) === String(c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    className="cb-cat cb-mycat"
                    style={{ width: "100%", textAlign: "left", background: on ? "var(--cb-soft)" : "transparent", border: 0, borderTop: "1px solid var(--cb-line)", cursor: "pointer", font: "inherit" }}
                    onClick={() => onPickCategory(on ? "" : c.id)}
                    aria-pressed={on}
                    aria-label={`${c.name}: you have paid ${formatNaira(c.paid)}${c.remaining ? `, ${formatNaira(c.remaining)} still due` : ""}. ${on ? "Showing its payments" : "Show its payments"}`}
                  >
                    <span className="name">
                      <KindChip kind={c.kind} />
                      <span style={{ minWidth: 0 }}>
                        <span style={{ display: "block", overflowWrap: "anywhere" }}>{c.name}</span>
                        <span className="cb-sub" style={{ fontWeight: 400 }}>
                          {c.lastPaymentAt ? `Last payment ${whenLabel(c.lastPaymentAt)}` : "No payments yet"}
                        </span>
                      </span>
                    </span>
                    <span>
                      <span className="money">{formatNaira(c.paid)}</span>
                      {c.perMember ? (
                        <>
                          <span className="cb-sub">{c.remaining ? `${formatNaira(c.remaining)} still due of ${formatNaira(c.perMember)}` : "Paid up"}</span>
                          <Progress value={c.progress ?? 0} tone={tone} label={`${c.name} progress`} />
                        </>
                      ) : c.pending ? (
                        <span className="cb-sub">{formatNaira(c.pending)} waiting for confirmation</span>
                      ) : null}
                    </span>
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {c.perMember ? <StatusPill status={c.status} /> : <Pill tone={tone}>{KIND_META[c.kind].label}</Pill>}
                      <FiChevronRight size={16} aria-hidden="true" style={{ color: "var(--cb-faint)" }} />
                    </span>
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
